import { useMemo, useState } from "react"
import { Label, Pie, PieChart } from "recharts"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@components/common/chart"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@components/common/table"
import { Button } from "@components/common/button"
import { cn, exportTableToExcel, getChartColor } from "@pma/utils/helpers"
import { ChevronDown, Download } from "lucide-react"
import { CompletionBar } from "../widgets"
import { resolveEntityStatus, } from "../analytics-engine"
import { DictStatus } from "@pma/types"

interface EmployeeRow {
  name: string
  total: number
  completed: number
  inProgress: number
  completionPct: number
}

export function EmployeeBreakdownTable({
  filteredTasks,
  statuses,
  translate,
  isHebrew,
}: {
  filteredTasks: any[]
  statuses: DictStatus[]
  translate: (key: string) => string
  isHebrew: boolean
}) {
  const employeeRows: EmployeeRow[] = useMemo(() => {
    const map = new Map<string, { total: number; completed: number }>()
    filteredTasks.forEach((task: any) => {
      const users = task?.responsible_id || []
      if (!Array.isArray(users)) return
      const display = resolveEntityStatus(task, statuses)
      const isDone = display.category === "completed"
      users.forEach((user: any) => {
        const name = typeof user === "object" ? user?.name || "Unknown" : `User ${user}`
        if (!map.has(name)) map.set(name, { total: 0, completed: 0 })
        const entry = map.get(name)!
        entry.total++
        if (isDone) entry.completed++
      })
    })
    return Array.from(map.entries())
      .map(([name, data]) => ({
        name,
        total: data.total,
        completed: data.completed,
        inProgress: data.total - data.completed,
        completionPct: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
      }))
      .sort((a, b) => b.total - a.total)
  }, [filteredTasks, statuses])

  const [employeeOpen, setEmployeeOpen] = useState(true)

  if (!employeeRows.length) return null

  return (
    <Card className='overflow-hidden border border-gray-100 shadow-sm transition-shadow duration-200 hover:shadow-md'>
      <CardHeader
        className='cursor-pointer select-none px-5 pb-3 pt-5'
        onClick={() => setEmployeeOpen(!employeeOpen)}
      >
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                !employeeOpen && "-rotate-90",
              )}
            />
            <div>
              <CardTitle className='text-sm font-semibold tracking-tight text-foreground'>
                {translate("Tasks by Employee")}
              </CardTitle>
              <CardDescription className='text-xs'>
                {translate("Task status breakdown per team member")}
                {" — "}
                {employeeRows.length} {translate("members")}
              </CardDescription>
            </div>
          </div>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            title={translate("Export")}
            onClick={() => {
              const rows = employeeRows.map((e) => ({
                [translate("Employee")]: e.name,
                [translate("tasks total")]: e.total,
                [translate("Completed")]: e.completed,
                [translate("In Progress")]: e.inProgress,
                [translate("completion")]: `${e.completionPct}%`,
              }))
              exportTableToExcel(rows, "tasks-by-employee")
            }}
          >
            <Download className='h-4 w-4' />
          </Button>
        </div>
      </CardHeader>
      {employeeOpen && (
        <CardContent className='px-0 pb-3'>
          <div className='flex justify-center pb-4'>
            <ChartContainer
              config={{ tasks: { label: translate("Tasks"), color: "#3b82f6" } }}
              className='aspect-square w-full max-w-[300px]'
            >
              <PieChart>
                <Pie
                  data={employeeRows.map((e, i) => ({
                    name: e.name,
                    value: e.total,
                    fill: getChartColor(i),
                  }))}
                  dataKey='value'
                  nameKey='name'
                  innerRadius='40%'
                  outerRadius='70%'
                  strokeWidth={2}
                >
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                        return (
                          <text
                            x={viewBox.cx}
                            y={viewBox.cy}
                            textAnchor='middle'
                            dominantBaseline='middle'
                          >
                            <tspan
                              x={viewBox.cx}
                              y={viewBox.cy}
                              className='fill-foreground text-2xl font-bold'
                            >
                              {employeeRows.reduce((sum, e) => sum + e.total, 0)}
                            </tspan>
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) + 20}
                              className='fill-muted-foreground text-xs'
                            >
                              {translate("Tasks")}
                            </tspan>
                          </text>
                        )
                      }
                    }}
                  />
                </Pie>
                <ChartTooltip content={<ChartTooltipContent />} />
              </PieChart>
            </ChartContainer>
          </div>

          <div className='overflow-x-auto'>
            <Table>
              <TableHeader>
                <TableRow className='hover:bg-transparent'>
                  <TableHead className='px-5 text-start'>{translate("Employee")}</TableHead>
                  <TableHead className='text-center'>{translate("tasks total")}</TableHead>
                  <TableHead className='text-center'>{translate("Completed")}</TableHead>
                  <TableHead className='text-center'>{translate("In Progress")}</TableHead>
                  <TableHead className='min-w-[140px]'>{translate("completion")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employeeRows.map((employee) => (
                  <TableRow key={employee.name} className='transition-colors hover:bg-gray-50/80'>
                    <TableCell className='px-5 text-sm font-medium'>{employee.name}</TableCell>
                    <TableCell className='text-center font-mono text-sm tabular-nums'>
                      {employee.total}
                    </TableCell>
                    <TableCell className='text-center font-mono text-sm tabular-nums text-green-700'>
                      {employee.completed}
                    </TableCell>
                    <TableCell className='text-center font-mono text-sm tabular-nums text-amber-600'>
                      {employee.inProgress}
                    </TableCell>
                    <TableCell>
                      <CompletionBar percent={employee.completionPct} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      )}
    </Card>
  )
}
